// A person's language test results, on their profile.
//
// Read-only by design. Sending, amending and deleting live in the Language
// Tests section so the same action never has two homes — this panel exists so
// staff can see a result while looking at the student, and get to the section
// in one click when they need to act on it.
//
// It shows both papers. A result sent before this person had a record appears
// here too: the API matches on their email address as well as on their id.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, GraduationCap } from 'lucide-react';
import { getLanguageTestsFor } from '@/lib/api';
import {
  formatDateTime, resultText, resultTone, testStatusMeta,
} from '@/lib/languageTests';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';

const RESULT_TONE_CLASS = {
  good: 'text-emerald-700',
  warn: 'text-amber-700',
  neutral: 'text-[#062B1A]',
};

export default function LanguageTestsCard({ targetType, targetId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!targetId) return;
    try {
      setLoading(true);
      const { data } = await getLanguageTestsFor(targetType, targetId);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error('Could not load language test results');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle
              className="flex items-center gap-2 text-base text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <GraduationCap className="size-4 text-[#D4AF37]" /> Language Tests
            </CardTitle>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#062B1A]/60">
              English proficiency and French placement results. Send a test, amend a score or
              delete a result in the Language Tests section.
            </p>
          </div>
          <Button variant="outline" asChild className="shrink-0">
            <Link to="/admin/language-tests">
              Language Tests <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="h-20 animate-pulse rounded-lg bg-[#F7F8FA]" />
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[#062B1A]/15 py-8 text-center text-sm text-[#062B1A]/45">
            No language test has been sent yet.
          </p>
        ) : (
          <div className="min-w-0 overflow-x-auto rounded-lg border border-[#062B1A]/8">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="bg-[#F7F8FA] text-xs uppercase tracking-[0.08em] text-[#062B1A]/50">
                <tr>
                  <th className="px-4 py-3">Test</th>
                  <th className="px-3 py-3">Sent</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Result</th>
                  <th className="px-3 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#062B1A]/8">
                {rows.map((row) => {
                  const meta = testStatusMeta(row.status);
                  return (
                    <tr key={row.id} className="text-[#062B1A]/75">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#062B1A]">{row.test_short_label}</p>
                        <p className="text-xs text-[#062B1A]/45">Attempt #{row.attempt_number}</p>
                      </td>
                      <td className="px-3 py-3">{formatDateTime(row.sent_at)}</td>
                      <td className="px-3 py-3">
                        <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
                      </td>
                      <td className="px-3 py-3">
                        {row.status === 'submitted' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className={cn('font-semibold', RESULT_TONE_CLASS[resultTone(row)])}>
                              {resultText(row)}
                            </span>
                            {row.overridden && (
                              <StatusPill tone="gold" title={row.override_reason}>Amended</StatusPill>
                            )}
                          </span>
                        ) : (
                          <span className="text-[#062B1A]/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">{formatDateTime(row.submitted_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
